import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  redirect,
  useParams,
  useSearch,
} from "@tanstack/react-router";
import { AppShell } from "./components/layout/AppShell";
import { ClaudeConnectionProvider } from "./components/layout/ClaudeConnectionContext";
import { CrumbsProvider, useCrumbsValue } from "./components/layout/CrumbsContext";
import { SettingsLayout } from "./components/layout/SettingsLayout";
import { requireAdmin, requireAuth } from "./lib/route-guards";
import { RootLayout } from "./routes/__root";
import { AccountPage } from "./routes/audit/account";
import { FlagsPage } from "./routes/audit/flags";
import { GuidePage } from "./routes/audit/guide";
import { SolutionsPage } from "./routes/audit/index";
import { NewAuditPage } from "./routes/audit/new-audit";
import { ReportingPage } from "./routes/audit/reporting";
import { RunPage } from "./routes/audit/run";
import { SolutionPage } from "./routes/audit/solution";
import { ProfilePage } from "./routes/dashboard/profile";
import { SettingsAuditPage } from "./routes/dashboard/settings/audit";
import { SettingsEmailPage } from "./routes/dashboard/settings/email";
import { SettingsSystemPage } from "./routes/dashboard/settings/system";
import { SettingsUsersPage } from "./routes/dashboard/settings/users";
import { LoginPage } from "./routes/login";
import { SignupPage } from "./routes/signup";

/**
 * Routes.
 *
 * The audit app's navy `AppShell` is the ONLY shell — settings, admin and profile
 * are hosted inside it too, so there is one navigation model rather than two.
 * The template's `DashboardLayout`/`Sidebar` are gone, and `/dashboard/*` URLs
 * redirect to their new homes so existing links and bookmarks keep working.
 *
 * Auth guards live on the layout route and are inherited, per
 * .claude/rules/frontend.md — never on individual routes.
 */

const rootRoute = createRootRoute({ component: RootLayout });

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  beforeLoad: () => {
    throw redirect({ to: "/solutions" });
  },
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  component: LoginPage,
});

const signupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/signup",
  component: SignupPage,
});

/**
 * The authenticated shell. `CrumbsProvider` wraps `AppShell` so a page rendered
 * in the outlet can publish its own breadcrumb trail — the useful labels are data
 * the page has already fetched, not something the URL carries.
 */
function AppShellRoute() {
  return (
    <ClaudeConnectionProvider>
      <CrumbsProvider>
        <ShellWithCrumbs />
      </CrumbsProvider>
    </ClaudeConnectionProvider>
  );
}

function ShellWithCrumbs() {
  const crumbs = useCrumbsValue();
  return (
    <AppShell crumbs={crumbs}>
      <Outlet />
    </AppShell>
  );
}

const appLayout = createRoute({
  getParentRoute: () => rootRoute,
  id: "app",
  beforeLoad: requireAuth,
  component: AppShellRoute,
});

const solutionsRoute = createRoute({
  getParentRoute: () => appLayout,
  path: "/solutions",
  component: SolutionsPage,
});

function SolutionRouteComponent() {
  const { slug } = useParams({ from: "/app/solutions/$slug" });
  return <SolutionPage slug={slug} />;
}

const solutionRoute = createRoute({
  getParentRoute: () => appLayout,
  path: "/solutions/$slug",
  component: SolutionRouteComponent,
});

function NewAuditRouteComponent() {
  // `solution` pre-selects the target from a solution page's "Audit a new
  // version" button. Optional, and unvalidated by design — a stale slug simply
  // leaves the picker empty rather than erroring the page.
  const { solution } = useSearch({ from: "/app/new-audit" });
  return <NewAuditPage solution={solution} />;
}

const newAuditRoute = createRoute({
  getParentRoute: () => appLayout,
  path: "/new-audit",
  validateSearch: (search: Record<string, unknown>): { solution?: string } => ({
    solution: typeof search.solution === "string" ? search.solution : undefined,
  }),
  component: NewAuditRouteComponent,
});

function RunRouteComponent() {
  const { reference } = useParams({ from: "/app/runs/$reference" });
  return <RunPage reference={reference} />;
}

const runRoute = createRoute({
  getParentRoute: () => appLayout,
  path: "/runs/$reference",
  component: RunRouteComponent,
});

function FlagsRouteComponent() {
  const { reference } = useParams({ from: "/app/runs/$reference/flags" });
  return <FlagsPage reference={reference} />;
}

const flagsRoute = createRoute({
  getParentRoute: () => appLayout,
  path: "/runs/$reference/flags",
  component: FlagsRouteComponent,
});

const guideRoute = createRoute({
  getParentRoute: () => appLayout,
  path: "/guide",
  component: GuidePage,
});

const reportingRoute = createRoute({
  getParentRoute: () => appLayout,
  path: "/reporting",
  component: ReportingPage,
});

const accountRoute = createRoute({
  getParentRoute: () => appLayout,
  path: "/account",
  component: AccountPage,
});

const profileRoute = createRoute({
  getParentRoute: () => appLayout,
  path: "/profile",
  component: ProfilePage,
});

function SettingsLayoutRoute() {
  return (
    <SettingsLayout>
      <Outlet />
    </SettingsLayout>
  );
}

const settingsLayout = createRoute({
  getParentRoute: () => appLayout,
  path: "/settings",
  beforeLoad: async (ctx) => {
    await requireAdmin();
    if (ctx.location.pathname === "/settings") {
      throw redirect({ to: "/settings/users" });
    }
  },
  component: SettingsLayoutRoute,
});

const settingsUsersRoute = createRoute({
  getParentRoute: () => settingsLayout,
  path: "/users",
  component: SettingsUsersPage,
});

const settingsSystemRoute = createRoute({
  getParentRoute: () => settingsLayout,
  path: "/system",
  component: SettingsSystemPage,
});

const settingsEmailRoute = createRoute({
  getParentRoute: () => settingsLayout,
  path: "/email",
  component: SettingsEmailPage,
});

const settingsAuditRoute = createRoute({
  getParentRoute: () => settingsLayout,
  path: "/audit",
  component: SettingsAuditPage,
});

/**
 * Old `/dashboard/*` URLs, kept as redirects rather than deleted. The dashboard
 * shell is gone, but bookmarks and any link already sent to someone are not, and
 * a 404 on those would look like the app broke.
 */
const legacyRedirects = [
  { path: "/dashboard", to: "/solutions" },
  { path: "/dashboard/profile", to: "/profile" },
  { path: "/dashboard/settings", to: "/settings/users" },
  { path: "/dashboard/settings/users", to: "/settings/users" },
  { path: "/dashboard/settings/system", to: "/settings/system" },
  { path: "/dashboard/settings/email", to: "/settings/email" },
  { path: "/dashboard/settings/audit", to: "/settings/audit" },
] as const;

const legacyRoutes = legacyRedirects.map(({ path, to }) =>
  createRoute({
    getParentRoute: () => rootRoute,
    path,
    beforeLoad: () => {
      throw redirect({ to });
    },
  }),
);

const routeTree = rootRoute.addChildren([
  indexRoute,
  loginRoute,
  signupRoute,
  ...legacyRoutes,
  appLayout.addChildren([
    solutionsRoute,
    solutionRoute,
    newAuditRoute,
    runRoute,
    flagsRoute,
    guideRoute,
    reportingRoute,
    accountRoute,
    profileRoute,
    settingsLayout.addChildren([
      settingsUsersRoute,
      settingsSystemRoute,
      settingsEmailRoute,
      settingsAuditRoute,
    ]),
  ]),
]);

export const router = createRouter({ routeTree });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
